import ConfirmationDot from '@/components/ui/verification/confirmation-dot.tsx';
import HoverClickStay from '@/components/ux/hover-click-stay';
import { useDealContext } from '@/contexts/deal-provider';
import { useDealByShortId } from '@/hooks/use-deals-selectors';
import { pdkAxios } from '@/lib/pdk-axios';
import type { Deal } from '@/models/registry';
import { dealStore } from '@/models/stores';
import { ConfirmationField, ConfirmationTargetType } from '@/models/types.ts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import InlineTextInput from '../../ui/inline-text-input';
import CoRegistrantDeleteButton from './co-registrant-delete-button';

const CoRegistrantNameEditor = observer(() => {
  const { currentShortId } = useDealContext();
  const deal = useDealByShortId(currentShortId);
  const dealId = deal?.id;
  const dealShortId = deal?.short_id;
  const coRegistrant = deal?.co_registrant;
  const fullName = coRegistrant?.full_name || '';
  const coRegistrantId = coRegistrant?.id;
  const [fullNameValue, setFullNameValue] = useState(fullName);

  const queryClient = useQueryClient();

  const updateCoRegistrantNameMutation = useMutation({
    onMutate: async ({ coRegistrantName }) => {
      if (!dealId) return;

      dealStore.updateCoRegistrantFullName({
        id: dealId,
        fullName: coRegistrantName,
      });
    },
    mutationFn: async ({
      coRegistrantName,
    }: {
      coRegistrantName: NonNullable<Deal['co_registrant']>['full_name'];
    }) => {
      const response = await pdkAxios
        .post(`/deals/${dealId}/co-registrant/${coRegistrantId}/name`, {
          full_name: coRegistrantName,
        })
        .catch((e) => {
          console.error('Error updating co-registrant name', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {},
    onError: (error, variables) => {
      console.error('Error updating co-registrant name', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealShortId] });
    },
  });

  const handleCoRegistrantNameChange = useCallback(
    debounce((newCoRegistrantName: string) => {
      updateCoRegistrantNameMutation.mutate({
        coRegistrantName: newCoRegistrantName,
      });
    }, 1000),
    [],
  );

  useEffect(() => {
    if (fullNameValue === fullName) return;

    handleCoRegistrantNameChange(fullNameValue);
  }, [fullNameValue]);

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFullNameValue(e.target.value);
  }, []);

  useEffect(() => {
    setFullNameValue(fullName);
  }, [fullName]);

  return (
    <div className="flex flex-col gap-2 w-full">
      <HoverClickStay
        key="co-registrant-hover"
        tether={
          dealId &&
          coRegistrantId && (
            <CoRegistrantDeleteButton
              dealId={dealId}
              coRegistrantId={coRegistrantId}
            />
          )
        }
      >
        <InlineTextInput
          key={`co-registrant-name-editor-${dealId}-title`}
          placeholder="Co-Registrant Name"
          value={fullNameValue}
          wrapperClassName="w-full"
          className="w-full h-fit text-black py-0 text-2xl!  tracking-tight"
          readOnly
        />
      </HoverClickStay>
      <div className="flex flex-row">
        <InlineTextInput
          key={`co-registrant-name-editor-${dealId}-input`}
          placeholder="Co-Registrant Name"
          value={fullNameValue}
          wrapperClassName="w-full"
          className="w-full!"
          onChange={onChange}
        />
        {dealId && (
          <ConfirmationDot
            targetId={dealId}
            targetType={ConfirmationTargetType.Deal}
            field={ConfirmationField.CoRegistrantName}
            value={observable.box(fullNameValue)}
          />
        )}
      </div>
    </div>
  );
});

export default CoRegistrantNameEditor;
